import React from 'react'
import { listOportunidades } from '@/modules/oportunidades/services/oportunidadesService'
import type { OpportunityItem } from '@/modules/oportunidades/types'
import { fetchThermometerMap } from '@/modules/termometro/services/thermometerService'

const CACHE_TTL_MS = 2 * 60 * 1000

let sharedMap: Record<string, OpportunityItem> = {}
let lastLoadedAt: number | null = null
let inflight: Promise<void> | null = null

async function ensureLoaded(options?: { force?: boolean }) {
  const force = options?.force ?? false
  const now = Date.now()

  if (!force && lastLoadedAt && now - lastLoadedAt < CACHE_TTL_MS) return

  if (!force && inflight) {
    await inflight
    return
  }

  inflight = (async () => {
    try {
      const items = await listOportunidades()
      const next: Record<string, OpportunityItem> = {}
      for (const it of items || []) {
        const n = Number(String(it.id).trim())
        if (!Number.isFinite(n) || n <= 0) continue
        next[String(Math.floor(n))] = it
      }
      sharedMap = next
    } finally {
      lastLoadedAt = Date.now()
      inflight = null
    }
  })()

  await inflight
}

type UseLinkedOpportunitiesMapResult = {
  map: Record<string, OpportunityItem>
  loading: boolean
}

export function useLinkedOpportunitiesMap(
  opportunityIds: number[],
  enabled: boolean,
): UseLinkedOpportunitiesMapResult {
  const [map, setMap] = React.useState<Record<string, OpportunityItem>>({})
  const [loading, setLoading] = React.useState(false)

  const ids = React.useMemo(
    () =>
      Array.from(
        new Set(
          (opportunityIds || [])
            .map((id) => Number(id))
            .filter((id) => Number.isFinite(id) && id > 0),
        ),
      ).map((id) => Math.floor(id)),
    [opportunityIds],
  )

  const idKey = React.useMemo(() => ids.join(','), [ids])

  const load = React.useCallback(async () => {
    if (!enabled || ids.length === 0) {
      setMap({})
      setLoading(false)
      return
    }

    setLoading(true)

    try {
      await ensureLoaded()

      const subset: Record<string, OpportunityItem> = {}
      for (const id of ids) {
        const key = String(id)
        if (sharedMap[key]) {
          subset[key] = sharedMap[key]
        }
      }

      try {
        const thermo = await fetchThermometerMap(ids.map((id) => String(id)))
        if (thermo.size > 0) {
          for (const id of ids) {
            const key = String(id)
            const it = subset[key]
            if (!it) continue
            const percent = thermo.get(key)?.percent ?? null
            if (percent != null) {
              subset[key] = { ...it, thermometerPercent: percent }
            }
          }
        }
      } catch {
        // fallback para confluencePercent
      }

      setMap(subset)
    } catch {
      setMap({})
    } finally {
      setLoading(false)
    }
  }, [enabled, idKey, ids])

  React.useEffect(() => {
    void load()
  }, [load])

  return { map, loading }
}
